import { epicycles, toSvgPath, visibleTrail } from "./math/epicycles";
import { createStrokeFourier } from "./math/strokeFourier";
import { koiSource } from "./shapes/koi";
import { clamp01 } from "./timeline";

export const KOI_DETAIL_START = 7.05;
export const KOI_DETAIL_END = 11.22;

const mainIds = [
  "dorsal", "pectoral", "pelvic", "rear-fin", "gill", "cheek", "lip", "mouth",
  "whisker-left", "whisker-right", "eye-outline", "eye-iris", "eye-pupil", "brow",
  "head-patch", "middle-patch", "rear-patch", "tail-patch",
] as const;

const schedule = [
  ...mainIds.map((id, index) => ({
    id,
    start: KOI_DETAIL_START + index * 0.175,
    duration: id === "dorsal" || id.endsWith("patch") ? 1 : 0.68,
    circles: id.endsWith("patch") ? 128 : 96,
  })),
  ...koiSource.filter((stroke) => stroke.id.startsWith("fin-ray-"))
    .map((stroke, index) => ({ id: stroke.id, start: 7.65 + index * 0.09, duration: 0.52, circles: 48 })),
  ...koiSource.filter((stroke) => stroke.id.startsWith("scale-"))
    .map((stroke, index) => ({ id: stroke.id, start: 9.5 + index * 0.075, duration: 0.36, circles: 48 })),
];

/** All 66 interior strokes have their own coefficients, timing and visible chain. */
export const koiDetailStrokes = schedule.map((item, index) => {
  const source = koiSource.find((stroke) => stroke.id === item.id);
  if (!source) throw new Error(`Missing koi detail stroke: ${item.id}`);
  const model = createStrokeFourier(source.path, source.closed, item.circles);
  const xs = model.sourceSamples.map((point) => point.x);
  const ys = model.sourceSamples.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const extent = Math.max(maxX - minX, maxY - minY);
  return {
    ...item,
    ...model,
    group: source.group,
    closed: source.closed,
    anchor: { x: 115 + (index % 3) * 130, y: 830 + (Math.floor(index / 3) % 4) * 125 },
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    leftScale: Math.min(2.2, 76 / extent),
  };
});

export const getKoiDetailFrame = (seconds: number) => ({
  complete: koiDetailStrokes.filter((stroke) => seconds >= stroke.start + stroke.duration),
  active: koiDetailStrokes
    .filter((stroke) => seconds >= stroke.start && seconds < stroke.start + stroke.duration)
    .map((stroke) => {
      const progress = clamp01((seconds - stroke.start) / stroke.duration);
      const chain = epicycles(stroke.terms, stroke.dc, progress * stroke.phaseEnd);
      const tip = chain[chain.length - 1].tip;
      return { ...stroke, chain, tip, path: toSvgPath(visibleTrail(stroke.trail, progress, tip)) };
    }),
});
